import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Product, Category, ApiResponse, AdminProductListItem } from '../../types';
import { api } from '../../services/api';
import { formatApiError } from '../../utils/apiError';
import { redirectSlugFrom } from '../../utils/slugRedirect';

interface ProductsState {
  products: Product[];
  categories: Category[];
  /** Admin: az összes termék (inaktív is) könnyű lista-alakban, lapozás nélkül. */
  adminProducts: AdminProductListItem[];
  /** Admin: az összes kategória (inaktív is), lapozás nélkül. */
  adminCategories: Category[];
  /** Az admin listák saját betöltés-/hibaállapota (független a bolt `loading`-jától). */
  adminProductsLoading: boolean;
  adminProductsError: string | null;
  adminCategoriesLoading: boolean;
  adminCategoriesError: string | null;
  categoryTree: Category[];
  currentProduct: Product | null;
  /** Régi termék-webcímnél az API által adott új slug (a termékoldal erre vált). */
  productRedirect: string | null;
  loading: boolean;
  error: string | null;
  pagination: {
    count: number;
    next: string | null;
    previous: string | null;
    currentPage: number;
  };
}

const initialState: ProductsState = {
  products: [],
  categories: [],
  adminProducts: [],
  adminCategories: [],
  adminProductsLoading: false,
  adminProductsError: null,
  adminCategoriesLoading: false,
  adminCategoriesError: null,
  categoryTree: [],
  currentProduct: null,
  productRedirect: null,
  loading: false,
  error: null,
  pagination: {
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
  },
};

// Async thunks
/** A /products/ lista szűrői (a terméklista oldal URL-jéből). */
export type ProductListParams = Record<string, string | number | boolean>;

export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (params?: ProductListParams) => {
    const response = await api.get('/products/', { params });
    return response.data as ApiResponse<Product>;
  }
);

/** Lapozatlan (tömb) vagy lapozott ({results}) választ is elfogad. */
const asList = <T,>(data: T[] | ApiResponse<T>): T[] =>
  Array.isArray(data) ? data : data.results;

export const fetchAdminProducts = createAsyncThunk<AdminProductListItem[], void, { rejectValue: string }>(
  'products/fetchAdminProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/products/');
      return asList(response.data as AdminProductListItem[] | ApiResponse<AdminProductListItem>);
    } catch (error: unknown) {
      return rejectWithValue(formatApiError(error));
    }
  }
);

export const fetchAdminCategories = createAsyncThunk<Category[], void, { rejectValue: string }>(
  'products/fetchAdminCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/categories/');
      return asList(response.data as Category[] | ApiResponse<Category>);
    } catch (error: unknown) {
      return rejectWithValue(formatApiError(error));
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'products/fetchCategories',
  async () => {
    const response = await api.get('/categories/');
    return response.data as ApiResponse<Category>;
  }
);

export const fetchCategoryTree = createAsyncThunk(
  'products/fetchCategoryTree',
  async () => {
    const response = await api.get('/categories/tree/');
    // Handle both paginated and non-paginated responses
    if (response.data.results) {
      return response.data.results as Category[];
    }
    return response.data as Category[];
  }
);

export const fetchProductBySlug = createAsyncThunk<
  Product,
  string,
  { rejectValue: { message: string; redirect: string | null } }
>('products/fetchProductBySlug', async (slug, { rejectWithValue }) => {
  try {
    const response = await api.get(`/products/${slug}/`);
    return response.data as Product;
  } catch (error: unknown) {
    const redirect = redirectSlugFrom(error);
    return rejectWithValue({
      message: redirect ? 'A termék új címre költözött.' : formatApiError(error),
      redirect,
    });
  }
});

export const fetchProductById = createAsyncThunk(
  'products/fetchProductById',
  async (id: string | number) => {
    const response = await api.get(`/products/by-id/${id}/`);
    return response.data as Product;
  }
);

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch products
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.results;
        // Carry the requested page through the thunk args so the UI shows
        // the correct pagination state instead of always "page 1".
        const requestedPage = Number(action.meta.arg?.page) || 1;
        state.pagination = {
          count: action.payload.count,
          next: action.payload.next,
          previous: action.payload.previous,
          currentPage: requestedPage,
        };
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch products';
      })
      // Fetch admin products (includes inactive products)
      .addCase(fetchAdminProducts.pending, (state) => {
        state.adminProductsLoading = true;
        state.adminProductsError = null;
      })
      .addCase(fetchAdminProducts.fulfilled, (state, action) => {
        state.adminProductsLoading = false;
        state.adminProducts = action.payload;
      })
      .addCase(fetchAdminProducts.rejected, (state, action) => {
        state.adminProductsLoading = false;
        state.adminProductsError = action.payload ?? 'Nem sikerült betölteni a termékeket.';
      })
      // Fetch categories
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload.results;
      })
      .addCase(fetchAdminCategories.pending, (state) => {
        state.adminCategoriesLoading = true;
        state.adminCategoriesError = null;
      })
      .addCase(fetchAdminCategories.fulfilled, (state, action) => {
        state.adminCategoriesLoading = false;
        state.adminCategories = action.payload;
      })
      .addCase(fetchAdminCategories.rejected, (state, action) => {
        state.adminCategoriesLoading = false;
        state.adminCategoriesError = action.payload ?? 'Nem sikerült betölteni a kategóriákat.';
      })
      // Fetch category tree
      .addCase(fetchCategoryTree.fulfilled, (state, action) => {
        state.categoryTree = action.payload;
      })
      // Fetch product by slug
      .addCase(fetchProductBySlug.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.productRedirect = null;
      })
      .addCase(fetchProductBySlug.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload;
      })
      .addCase(fetchProductBySlug.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message ?? action.error.message ?? 'Failed to fetch product';
        state.productRedirect = action.payload?.redirect ?? null;
      })
      // Fetch product by ID
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch product';
      });
  },
});

export const { clearCurrentProduct, clearError } = productsSlice.actions;
export default productsSlice.reducer;